/**
 * AI 智能分类 · Selectors（v4 · 对齐 demo SSE 协议）
 *
 * 统一从这里消费 state，组件不要直接读 store 子树路径。
 */

import { createSelector } from '@reduxjs/toolkit'
import type { RootState } from '../../app/store'
import type { AssistantMessage, BottomOption } from '../../types/ai-classify'
import { adapter as messagesAdapter } from './messagesSlice'

// ============================================================
// Session
// ============================================================

export const selectSession = (s: RootState) => s.aiClassify.session
export const selectConversationId = (s: RootState) =>
  s.aiClassify.session.conversationId
export const selectSessionStatus = (s: RootState) => s.aiClassify.session.status
export const selectSessionError = (s: RootState) => s.aiClassify.session.error
/** /init 给的 recommended_prompts · QuickPrompts 用 */
export const selectInitRecommended = (s: RootState) =>
  s.aiClassify.session.initRecommended
/** /init 给的 note · 首屏 welcome 文案 */
export const selectInitNote = (s: RootState) => s.aiClassify.session.initNote
/** init 接口是否还在飞行中 · 骨架屏用 */
export const selectInitLoading = (s: RootState) =>
  s.aiClassify.session.initLoading

// ============================================================
// Messages
// ============================================================

const messagesSelectors = messagesAdapter.getSelectors<RootState>(
  (s) => s.aiClassify.messages
)

/** 有序全部消息 · ChatFeed 直接 map 渲染 */
export const selectAllMessages = messagesSelectors.selectAll

/**
 * 会话是否处于缺省态（无任何消息）。
 * 新对话/清空后为 true；发送消息（SSE 会话）或恢复历史会话后为 false。
 * ChatFeed 欢迎区与 Composer 问卷入口的显隐都以此为准。
 */
export const selectChatIsEmpty = createSelector(
  [selectAllMessages],
  (messages) => messages.length === 0
)

/**
 * 最后一条 done 状态的 assistant 消息 · 主要用于读取最新的 bottomOptions
 */
export const selectLatestDoneAssistant = createSelector(
  [selectAllMessages],
  (messages): AssistantMessage | null => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (m.kind === 'assistant' && m.status === 'done') return m
    }
    return null
  }
)

/**
 * 当前 streaming 中的 assistant 消息 · 同一时刻最多一条
 */
export const selectStreamingAssistant = createSelector(
  [selectAllMessages],
  (messages): AssistantMessage | null => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (m.kind === 'assistant' && m.status === 'streaming') return m
    }
    return null
  }
)

/**
 * 最近一条 done assistant 消息的 bottomOptions
 *
 * 没有就返回空数组，便于 Composer 直接 map 渲染。
 */
export const selectLatestBottomOptions = createSelector(
  [selectLatestDoneAssistant],
  (msg): BottomOption[] => msg?.payload?.bottomOptions ?? []
)

/**
 * 判断某条 assistant 消息是否"已被回答"
 *
 * 语义：消息列表里这条消息之后还出现过 user TextMessage → 用户已做出选择 →
 * 这条消息的卡片应该锁死（next_level / ask 的 options 不能再点）。
 *
 * 用法：MessageAssistant 在渲染 ChoiceOptionsCard 时调，已回答 → disabled=true。
 */
export const selectIsMessageAnswered =
  (messageId: string) =>
  (state: RootState): boolean => {
    const messages = selectAllMessages(state)
    const idx = messages.findIndex((m) => m.id === messageId)
    if (idx < 0) return false
    for (let i = idx + 1; i < messages.length; i++) {
      const m = messages[i]
      if (m.kind === 'text' && m.role === 'user') return true
    }
    return false
  }

/**
 * folder_plan 卡片的"被消费"状态 · 决定锁死与否及锁死成因
 *  · none       · 未被消费，仍可编辑
 *  · confirmed  · 已被建夹任务消费（用户点"确认创建"或文本路径确认）
 *  · superseded · 被新一轮 refine 取代（用户改了维度出了新方案），从未被确认
 */
export type FolderPlanConsumedState = 'none' | 'confirmed' | 'superseded'

/**
 * 判断某条 folder_plan assistant 消息是否已被消费 (转 readonly) 及成因。
 *
 * 向后扫描，**先遇到哪种卡决定成因**：
 *   · 先遇到 build_task_status → confirmed（中间没有新方案卡，任务必属本卡）
 *   · 先遇到另一条 folder_plan → superseded（本卡被新方案取代；即便之后再出现
 *     build_task_status，那个任务属于新卡，不能把本卡标成"已确认"）
 *
 * 用途: usePlanPreview 据此派生 readonly + 标记文案（confirmed 才显示"已确认"，
 * superseded 仅置灰不标记），弥补 card.taskCreated 字段在 "refine 取代" 路径下
 * 不会被回写的盲区。
 *
 * 只在 LLM 给出"真正的替代物" (新 folder_plan / build_task) 时才锁老卡。
 * 语义更准, 误锁风险更低。
 *
 * 如果产品上希望 "用户一发新消息就锁老卡, 强迫确认一次意图",
 * 再把 user TextMessage 加进判定即可 (一行改动)。
 */
export const selectFolderPlanConsumedState =
  (messageId: string) =>
  (state: RootState): FolderPlanConsumedState => {
    const messages = selectAllMessages(state)
    const idx = messages.findIndex((m) => m.id === messageId)
    if (idx < 0) return 'none'
    for (let i = idx + 1; i < messages.length; i++) {
      const m = messages[i]
      if (m.kind !== 'assistant') continue
      const cardKind = m.payload?.card?.kind
      if (cardKind === 'build_task_status') return 'confirmed'
      if (cardKind === 'folder_plan') return 'superseded'
    }
    return 'none'
  }

/**
 * 会话历史里是否曾经出现过 build_task_status 卡片 = 用户已经至少建过一次夹
 *
 * (v5 的 creation_done 信号已下线, 改用 build_task_status; 历史 type='task'
 *  消息也被映射成 build_task_status, 兼容老历史会话。)
 *
 * 用途：判断当前 final_proposal 是不是"追加分类"场景。
 *   · 已建过 → 新 plan 默认 0 选中，避免误把整批方案再建一次（吴皇呈追加 bug）
 *   · 没建过 → 首次方案，按现有"前 5 项默认勾选"语义保持
 */
export const selectHasPriorCreation = createSelector(
  [selectAllMessages],
  (messages): boolean => {
    for (const m of messages) {
      if (m.kind !== 'assistant') continue
      const card = m.payload?.card
      if (card && card.kind === 'build_task_status') return true
    }
    return false
  }
)

/**
 * 取某条 assistant 消息后的"用户回答文本"
 *
 * 找消息列表中本消息之后第一条 user TextMessage 的 content。
 * 没找到（还没回答）→ null。
 *
 * 用法：ChoiceOptionsCard 已回答时，对比 options[].label 找出选了哪条 → 折叠未选项。
 */
export const selectAnswerForMessage =
  (messageId: string) =>
  (state: RootState): string | null => {
    const messages = selectAllMessages(state)
    const idx = messages.findIndex((m) => m.id === messageId)
    if (idx < 0) return null
    for (let i = idx + 1; i < messages.length; i++) {
      const m = messages[i]
      if (m.kind === 'text' && m.role === 'user') return m.content
    }
    return null
  }

// ============================================================
// UI
// ============================================================

export const selectSidebarOpen = (s: RootState) => s.aiClassify.ui.sidebarOpen
export const selectSidebarWidth = (s: RootState) => s.aiClassify.ui.sidebarWidth
/** 列表页"多选模式"开关 · true = 进入勾选态 */
export const selectManageMode = (s: RootState) => s.aiClassify.ui.manageMode
/** 多选模式下勾选的真实 folderID 集合 */
export const selectManageSelectedFolderIds = (s: RootState) =>
  s.aiClassify.ui.manageSelectedFolderIds
/** 列表视图模式 · grid / list */
export const selectViewMode = (s: RootState) => s.aiClassify.ui.viewMode

/** 文件夹列表排序 · 宫格 / 列表共用 */
export const selectFolderSort = (s: RootState) => s.aiClassify.ui.folderSort

/** AI 助手 · 历史会话 overlay 是否展开 */
export const selectHistoryOverlayOpen = (s: RootState) =>
  s.aiClassify.ui.historyOverlayOpen

/** AI 助手 · 新对话动画序列号 · 用于 React key 触发 slide-up 重挂载 */
export const selectNewConvAnimSeq = (s: RootState) =>
  s.aiClassify.ui.newConvAnimSeq

/**
 * Composer 顶部「当前文件夹」上下文 · 仅文件夹详情页有值，列表页为 null。
 * Composer 路径条据此显隐 + 取 path 展示；folderId 预留作会话上下文。
 */
export const selectComposerFolderContext = (s: RootState) =>
  s.aiClassify.ui.composerFolderContext

// ============================================================
// FolderSync
// ============================================================

export const selectFolderRefreshTick = (s: RootState) =>
  s.aiClassify.folderSync.refreshTick

export const selectRecentlyCreatedFolderIds = (s: RootState) =>
  s.aiClassify.folderSync.recentlyCreatedIds

/** 新建未读 NEW 角标 · 用户进入 folder 后由 markFolderEntered 出队 */
export const selectNewBadgeFolderIds = (s: RootState) =>
  s.aiClassify.folderSync.newBadgeFolderIds

// ============================================================
// FolderList（顶层文件夹列表 · 数据源 slice）
// ============================================================

/** 顶层文件夹（已 adapt）· 不含待归档虚拟项 */
export const selectFolderListItems = (s: RootState) =>
  s.aiClassify.folderList.items
/** 未归类（待归档）照片数 */
export const selectUnclassifiedPhotoCount = (s: RootState) =>
  s.aiClassify.folderList.unclassifiedPhotoCount
export const selectFolderListError = (s: RootState) =>
  s.aiClassify.folderList.error
/** idle 视作加载中 · 避免首帧 / 切团队后空态闪现 */
export const selectFolderListLoading = (s: RootState) => {
  const status = s.aiClassify.folderList.status
  return status === 'idle' || status === 'loading'
}
/**
 * 是否有文件夹 · 顶部工具栏显隐据此判断。
 * 用 items 而非 status：后台 refresh（status 瞬时 loading）时工具栏不闪没；
 * pending 不清空 items，切团队由 groupID 变化触发重新加载覆盖。
 */
export const selectHasFolders = (s: RootState) =>
  s.aiClassify.folderList.items.length > 0
/** 列表是否已成功加载过 · 侧边栏默认开合（applySidebarDefault）的就绪信号 */
export const selectFolderListLoaded = (s: RootState) =>
  s.aiClassify.folderList.status === 'succeeded'

// ============================================================
// Task（异步归档任务）
// ============================================================

export const selectAllTasks = (s: RootState) => s.aiClassify.task.tasks

/** 仍在归档中（用于轮询 / "进行中"列表段） */
export const selectActiveTasks = createSelector([selectAllTasks], (tasks) =>
  tasks.filter((t) => t.status === 'archiving')
)

/** 已完成 / 超时（用于"已完成"列表段） */
export const selectFinishedTasks = createSelector([selectAllTasks], (tasks) =>
  tasks.filter((t) => t.status !== 'archiving')
)

/** 红点闪烁标志 · 抽屉头部「分类任务」按钮 visual cue */
export const selectHasUnreadCompletion = (s: RootState) =>
  s.aiClassify.task.hasUnreadCompletion

/** 待弹 toast 通知的 taskId 队列 · ArchiveTaskCompletionToasts 订阅 */
export const selectPendingTaskNotifications = (s: RootState) =>
  s.aiClassify.task.pendingNotifications
